// -------------------------------------------------
// src/features/stock/components/AttributesManagement.tsx
// -------------------------------------------------

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Attribute, NewAttributePayload } from '../api/attributeApi';
import {
  fetchAttributesApi,
  createAttributeApi,
  deleteAttributeApi,
} from '../api/attributeApi';
import MinimalisticTable, { ColumnDef } from '../../../../shared/components/ui/MinimalisticTable';

export default function AttributesManagement() {
  const queryClient = useQueryClient();

  // ─── 1) Fetch (and cache) existing attributes ───────────────────────────────
  const {
    data: attributes = [],
    isLoading: isAttrsLoading,
    error: attrsError,
  } = useQuery<Attribute[], Error>({
    queryKey: ['productManagement', 'attributes'],  // ← this is the exact key we must invalidate
    queryFn: fetchAttributesApi,
    staleTime: 60_000,
  });

  // ─── 2) “Create” mutation ────────────────────────────────────────────────────
  const createAttributeMutation = useMutation<Attribute, Error, NewAttributePayload>({
    mutationFn: (payload) => createAttributeApi(payload),
    onSuccess: () => {
     // after a new attribute is created, refresh both “attributes‐management” and the global “allAttributes” list:
     queryClient.invalidateQueries({ queryKey: ['productManagement', 'attributes'] });
     queryClient.invalidateQueries({ queryKey: ['allAttributes'] });
      setIsAdding(false);
      setNewName('');
    },
  });

  // ─── 3) “Delete” mutation ────────────────────────────────────────────────────
  const deleteAttributeMutation = useMutation<void, Error, { attribute_id: number }>(
    {
      mutationFn: ({ attribute_id }) => deleteAttributeApi({ attribute_id }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['productManagement', 'attributes'] });
      },
    }
  );

  // ─── 4) Local state for inline “new attribute” row ────────────────────────────
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [newName, setNewName] = useState<string>('');

  const handleNewClick = () => {
    setIsAdding(true);
    setNewName('');
  };

  const handleAcceptNewAttribute = () => {
    const trimmed = newName.trim();
    if (trimmed === '') return;
    createAttributeMutation.mutate({
      id_category: 6,
      attribute_name: trimmed,
    });
  };

  const handleCancelNewAttribute = () => {
    setIsAdding(false);
    setNewName('');
  };

  // ─── 5) Loading / error UI ────────────────────────────────────────────────
  if (isAttrsLoading) {
    return <div className="text-sm text-neutral-400">Loading attributes…</div>;
  }
  if (attrsError) {
    return <div className="text-sm text-rose-400">Error: {attrsError.message}</div>;
  }

  // ─── 6) Table columns ────────────────────────────────────────────────────
  type AttributeRow = Attribute & { __isNew?: boolean };

  const columns: ColumnDef<AttributeRow>[] = [
    { header: 'ID', accessor: 'id_attribute' },
    { header: 'Creation Date', accessor: 'timestamp' },
    { header: 'Name', accessor: 'attribute_name' },
    {
      header: 'Actions',
      accessor: '',
      Cell: (_, row) => (
        <div className="flex space-x-2">
          <button
            onClick={() => {
              /* placeholder for updateAttributeApi(...) */
            }}
            className="text-amber-400 hover:text-amber-300"
          >
            Modify
          </button>
          <button
            onClick={() => deleteAttributeMutation.mutate({ attribute_id: row.id_attribute })}
            className="text-rose-400 hover:text-rose-300"
            disabled={deleteAttributeMutation.status === 'pending'}
          >
            {deleteAttributeMutation.status === 'pending' ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      ),
    },
  ];

  // ─── 7) Build the “new‐row” + existing data ────────────────────────────────
  const tableData: AttributeRow[] = isAdding
    ? [
        {
          id_attribute: 0,
          attribute_name: '',
          timestamp: '',
          __isNew: true,
        } as AttributeRow,
        ...attributes,
      ]
    : attributes;

  return (
    <div className="bg-neutral-900 p-4 rounded-md">
      <MinimalisticTable<AttributeRow>
        columns={columns}
        data={tableData}
        noDataMessage="No attributes found."

        /** Show “New” button in last column header */
        showNewButton={!isAdding}
        onNew={handleNewClick}

        /** Only “attribute_name” is editable in the new‐row */
        editableAccessors={['attribute_name']}
        newRowValues={{ attribute_name: newName }}
        onNewRowChange={(accessor, val) => {
          if (accessor === 'attribute_name') setNewName(val);
        }}

        /** Accept/Cancel for inline new‐row */
        onAcceptNew={handleAcceptNewAttribute}
        onCancelNew={handleCancelNewAttribute}
      />
    </div>
  );
}
